"use client"

import { useState } from "react"
import { useParams } from "@tanstack/react-router"
import { Plus } from "lucide-react"

import { AddCandidateSheet } from "@/components/jobs/add-candidate-sheet"
import { useGetJobsJobId } from "openapi/api/endpoints"

/**
 * The pipeline's way in to canvas 19's add-candidate sheet.
 *
 * This component used to carry the four write paths itself — a LinkedIn post, a
 * résumé upload, a manual create, and a talent-pool popover sitting beside a
 * tabbed dialog. All four live in the sheet now, under one stage line, so what
 * is left here is the trigger and the job the sheet writes against.
 */

/** Which surface 18c's empty state asked for, if any. */
export type AddCandidateSource = "pool" | "new"

export function AddCandidateDialog({
  requested,
  onRequestHandled,
}: {
  requested?: AddCandidateSource | null
  onRequestHandled?: () => void
} = {}) {
  const params = useParams({ strict: false }) as { jobId: string }
  const jobId = params.jobId
  const { data: job } = useGetJobsJobId(jobId, { query: { enabled: !!jobId } })

  const [open, setOpen] = useState(false)
  const isOpen = open || Boolean(requested)

  const close = () => {
    setOpen(false)
    onRequestHandled?.()
  }

  return (
    <>
      {/*
        19a draws this as a quiet teal label with a plus, not a bordered pill:
        "text instead of pills" is the same rule §18 applied to New note.
      */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-[7px] whitespace-nowrap text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80"
      >
        <Plus className="h-3.5 w-3.5" />
        Add candidate
      </button>

      <AddCandidateSheet
        jobId={jobId}
        jobTitle={job?.title}
        companyName={job?.company_name}
        isOpen={isOpen}
        onClose={close}
        /* 18c's two routes are the same sheet on different tabs. */
        initialSource={requested === "new" ? "linkedin" : "pool"}
      />
    </>
  )
}
